import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, Gift, Clock, Sparkles, ArrowRight, Zap } from 'lucide-react';

const PROMO_CODE = 'WELCOME10';
const PROMO_DURATION_MS = 10 * 60 * 1000; // 10 minutes

// Countdown Timer Component
export const CountdownTimer = ({ onExpire, size = 'default' }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [promoStartedAt, setPromoStartedAt] = useState(null);

  useEffect(() => {
    // Check if promo already started
    let startTime = localStorage.getItem('promo_started_at');
    
    if (!startTime) {
      // Start new promo
      startTime = new Date().toISOString();
      localStorage.setItem('promo_started_at', startTime);
      localStorage.setItem('promo_code', PROMO_CODE);
    }
    
    setPromoStartedAt(startTime);
    
    const calculateTimeLeft = () => {
      const start = new Date(startTime).getTime();
      const now = Date.now();
      const elapsed = now - start;
      const remaining = PROMO_DURATION_MS - elapsed;
      
      if (remaining <= 0) {
        setTimeLeft(0);
        onExpire?.();
        return 0;
      }
      
      setTimeLeft(remaining);
      return remaining;
    };
    
    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    
    return () => clearInterval(interval);
  }, [onExpire]);

  if (timeLeft === null) return null;
  if (timeLeft <= 0) return null;

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);

  if (size === 'small') {
    return (
      <span className="font-mono font-bold text-amber-600">
        {minutes}:{seconds.toString().padStart(2, '0')}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Clock className="w-5 h-5 text-amber-500 animate-pulse" />
      <div className="flex gap-1 items-center">
        <div className="bg-slate-900 text-white px-3 py-2 rounded-lg font-mono text-2xl font-bold min-w-[3rem] text-center">
          {minutes.toString().padStart(2, '0')}
        </div>
        <span className="text-slate-900 font-bold text-2xl">:</span>
        <div className="bg-slate-900 text-white px-3 py-2 rounded-lg font-mono text-2xl font-bold min-w-[3rem] text-center">
          {seconds.toString().padStart(2, '0')}
        </div>
      </div>
    </div>
  );
};

// Get promo data for registration
export const getPromoData = () => {
  const promoStartedAt = localStorage.getItem('promo_started_at');
  const promoCode = localStorage.getItem('promo_code');
  
  if (!promoStartedAt || !promoCode) return null;
  
  // Check if still valid
  const start = new Date(promoStartedAt).getTime();
  const now = Date.now();
  const elapsed = now - start;
  
  if (elapsed > PROMO_DURATION_MS) {
    // Expired - clear storage
    localStorage.removeItem('promo_started_at');
    localStorage.removeItem('promo_code');
    return null;
  }
  
  return {
    promo_code: promoCode,
    promo_started_at: promoStartedAt
  };
};

// Exit Intent Popup Component
export const ExitIntentPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [promoExpired, setPromoExpired] = useState(false);

  const handleMouseLeave = useCallback((e) => {
    // Only trigger when mouse leaves from the top of the viewport
    if (e.clientY <= 0 && !hasShown && !promoExpired) {
      setShowPopup(true);
      setHasShown(true);
      // Mark as shown in session
      sessionStorage.setItem('exit_popup_shown', 'true');
    }
  }, [hasShown, promoExpired]);

  useEffect(() => {
    // Check if already shown this session
    if (sessionStorage.getItem('exit_popup_shown')) {
      setHasShown(true);
      return;
    }

    // Add exit intent listener
    document.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave]);

  const handleClose = () => {
    setShowPopup(false);
  };

  return (
    <AnimatePresence>
      {showPopup && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]"
            onClick={handleClose}
          />
          
          {/* Popup - Centered Container */}
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="w-full max-w-lg pointer-events-auto"
            >
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 p-6 text-white relative">
                  <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white/20 rounded-full">
                      <Gift className="w-6 h-6" />
                    </div>
                    <span className="text-white/90 font-medium">Wait! Don't miss out...</span>
                  </div>
                  
                  <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                    Your FREE Upgrade is Waiting!
                  </h2>
                  <p className="text-white/80">
                    Sign up now and get upgraded to our Starter plan absolutely FREE
                  </p>
                </div>
              
              {/* Body */}
              <div className="p-6">
                {/* Timer */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="text-amber-800 font-medium text-sm mb-1">Offer expires in:</p>
                      <CountdownTimer onExpire={() => setPromoExpired(true)} />
                    </div>
                    <Zap className="w-12 h-12 text-amber-400" />
                  </div>
                </div>
                
                {/* Benefits */}
                <div className="space-y-3 mb-6">
                  <p className="font-semibold text-slate-900">You'll get:</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: '30 General Credits', value: '(+20 bonus)' },
                      { label: '7 Story Credits', value: '(+5 bonus)' },
                      { label: '7 Character Credits', value: '(+5 bonus)' },
                      { label: '4 Art Style Credits', value: '(+3 bonus)' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Sparkles className="w-4 h-4 text-violet-500 flex-shrink-0" />
                        <span className="text-slate-700">{item.label}</span>
                        <span className="text-emerald-600 font-medium">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* CTA */}
                <Link
                  to="/register"
                  onClick={handleClose}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-full font-bold text-lg hover:from-violet-500 hover:to-fuchsia-500 transition-all shadow-lg hover:shadow-xl"
                >
                  <Gift className="w-5 h-5" />
                  Claim My FREE Upgrade
                  <ArrowRight className="w-5 h-5" />
                </Link>
                
                <p className="text-center text-slate-500 text-sm mt-4">
                  No credit card required • Cancel anytime
                </p>
              </div>
            </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

// Promo Banner Component (for top of page)
export const PromoBanner = ({ className = '' }) => {
  const [promoExpired, setPromoExpired] = useState(false);
  
  if (promoExpired) return null;
  
  return (
    <div className={`bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 py-2 px-4 ${className}`}>
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 sm:gap-4 text-sm sm:text-base flex-wrap">
        <span className="flex items-center gap-1">
          <Zap className="w-4 h-4" />
          <span className="font-bold">LIMITED TIME:</span>
        </span>
        <span>Free Starter Plan upgrade!</span>
        <span className="flex items-center gap-1 font-mono bg-white/30 px-2 py-0.5 rounded">
          <Clock className="w-3 h-3" />
          <CountdownTimer onExpire={() => setPromoExpired(true)} size="small" />
        </span>
        <Link 
          to="/register"
          className="bg-slate-900 text-white px-3 py-1 rounded-full text-sm font-medium hover:bg-slate-800 transition-colors"
        >
          Claim Now →
        </Link>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
